// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NewStep06ExtraLocations - V38: Extra locaties
 * Garagebox, opslagbox, bedrijfspand, loods, werkplaats, perceel grond, anders.
 * Geen WizardAnswers-wijziging: moduleKey 'extraLocations' via wizardSave.
 */
import { useState, useEffect, useCallback } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { Input }      from '@/components/ui/Input'
import { Card }       from '@/components/ui/Card'
import { Button }     from '@/components/ui/Button'
import { useAuthStore } from '@/store'
import { useAutosave } from '@/hooks/useAutosave'
import { wizardLoad, wizardSave, ORV_INSURERS } from '@/services/wizard'
import { SelectWithOther } from '@/components/ui/SelectWithOther'
import { saveModuleProgress } from '@/services/profile'
import { generateUUID } from '@/lib/utils'
import type { SaveStatus } from '@/hooks/useAutosave'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExtraLocationEntry {
  id:              string
  type:            string   // 'garagebox'|'opslagbox'|'self-storage'|'bedrijfspand'|'loods'|'werkplaats'|'perceel'|'anders'
  typeOther:       string
  ownership:       string   // 'koop'|'huur'|'anders'
  address:         string   // legacy - één veld uit V38
  street:          string   // V42
  houseNumber:     string   // V42
  houseNumberAddition: string  // V42
  postalCode:      string   // V42
  city:            string   // V42
  // Koop-velden
  mortgageProvider: string
  mortgageProviderOther: string
  mortgageNumber:  string
  mortgageContact: string
  hasOrv:          string   // 'yes'|'no'|'unknown'
  orvInsurer:      string
  orvPolicyNr:     string
  orvContact:      string
  // Huur-velden
  landlord:        string
  contractNr:      string
  landlordContact: string
  // Alle
  keys:            string
  docsLocation:    string
  notes:           string
}

interface ExtraLocationsData {
  hasExtraLocations: string   // 'yes'|'no'|''
  locations: ExtraLocationEntry[]
}

const EMPTY_ENTRY = (): ExtraLocationEntry => ({
  id: generateUUID(),
  type: '', typeOther: '', ownership: '', address: '',
  street: '', houseNumber: '', houseNumberAddition: '', postalCode: '', city: '',
  mortgageProvider: '', mortgageProviderOther: '', mortgageNumber: '', mortgageContact: '',
  hasOrv: '', orvInsurer: '', orvPolicyNr: '', orvContact: '',
  landlord: '', contractNr: '', landlordContact: '',
  keys: '', docsLocation: '', notes: '',
})

const EMPTY_DATA: ExtraLocationsData = { hasExtraLocations: '', locations: [] }

const LOCATION_TYPES = [
  { value: 'garagebox',    label: 'Garagebox' },
  { value: 'opslagbox',    label: 'Opslagbox' },
  { value: 'self-storage', label: 'Self-storage' },
  { value: 'bedrijfspand', label: 'Bedrijfspand' },
  { value: 'loods',        label: 'Loods' },
  { value: 'werkplaats',   label: 'Werkplaats' },
  { value: 'perceel',      label: 'Perceel grond' },
  { value: 'anders',       label: 'Anders' },
]

const MORTGAGE_PROVIDERS = [
  'ING','Rabobank','ABN AMRO','Nationale Nederlanden','a.s.r.',
  'Obvion','Florius','BLG Wonen','SNS','RegioBank','Anders',
]

const DOCS_LOCATIONS = ['Thuis','Partner','Notaris','Administratie','Digitale kluis','Vertrouwenspersoon','Anders']

const MODULE_KEY = 'extraLocations'

// ── Component ─────────────────────────────────────────────────────────────────

export function NewStep06ExtraLocations({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { userId }          = useAuthStore()
  const [data,  setData]    = useState<ExtraLocationsData>(EMPTY_DATA)
  const [ready, setReady]   = useState(false)

  useEffect(() => {
    if (!userId) return
    onSaveStatus('idle')
    wizardLoad<ExtraLocationsData>(userId, MODULE_KEY).then(d => {
      if (d) setData({ ...EMPTY_DATA, ...d })
      setReady(true)
    })
  }, [userId, onSaveStatus])

  const save = useCallback(async (d: ExtraLocationsData) => {
    if (userId) {
      await wizardSave(userId, MODULE_KEY, d, d.locations.length)
      await saveModuleProgress(userId, 'extraLocations', d.locations.length > 0 ? 1 : 0, 1)
    }
  }, [userId])

  const { status } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function setHas(v: string) {
    setData(p => ({
      ...p,
      hasExtraLocations: v,
      locations: v === 'yes' && p.locations.length === 0 ? [EMPTY_ENTRY()] : p.locations,
    }))
  }

  function updEntry(id: string, field: keyof ExtraLocationEntry, value: string) {
    setData(p => ({
      ...p,
      locations: p.locations.map(loc => loc.id === id ? { ...loc, [field]: value } : loc),
    }))
  }

  function addEntry() {
    setData(p => ({ ...p, locations: [...p.locations, EMPTY_ENTRY()] }))
  }

  function removeEntry(id: string) {
    setData(p => {
      const updated = p.locations.filter(loc => loc.id !== id)
      return { ...p, locations: updated, hasExtraLocations: updated.length === 0 ? '' : p.hasExtraLocations }
    })
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Garage, opslag of bedrijfspand</h2>
        <p className="text-sm text-ink-3">
          Heeft u een garagebox, opslagruimte, bedrijfspand of andere locatie buiten uw
          woonadres? Leg dit vast zodat uw nabestaanden deze locaties niet over het hoofd zien.
        </p>
      </div>

      <InfoBlock title="Goed om te weten - extra locaties" variant="neutral" defaultOpen={false}>
        <p>
          Nabestaanden weten vaak niet van garageboxen, opslagruimtes of bedrijfspanden.
          Huurcontracten, sleutels en documenten kunnen verloren gaan als deze locaties niet zijn vastgelegd.
        </p>
        <p>
          <strong>Huurcontract:</strong> loopt automatisch door na overlijden totdat het formeel wordt opgezegd.
          Kosten blijven doorlopen - nabestaanden moeten tijdig handelen.
        </p>
        <p className="text-xs text-ink-4 mt-2 pt-2 border-t border-sand-200 leading-relaxed">
          WilsApp legt uw wensen vast als persoonlijke leidraad. Voor officiële registratie
          verwijzen wij naar de bevoegde instantie.
        </p>
      </InfoBlock>

      {/* Ja / Nee keuze */}
      <div className="flex flex-col gap-2">
        {[
          { value: 'yes', label: 'Ja, ik heb een of meer van dit soort locaties' },
          { value: 'no',  label: 'Nee' },
        ].map(opt => (
          <button key={opt.value} type="button" onClick={() => setHas(opt.value)}
            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${
              data.hasExtraLocations === opt.value
                ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
            }`}>
            <div className={`h-5 w-5 rounded-full border-2 flex-shrink-0 ${
              data.hasExtraLocations === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
            }`} />
            <span className="text-base font-semibold text-ink-2">{opt.label}</span>
          </button>
        ))}
      </div>

      {/* Locatielijst */}
      {data.hasExtraLocations === 'yes' && (
        <div className="flex flex-col gap-4 animate-slide-down">
          {data.locations.map((loc, idx) => (
            <Card key={loc.id}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-ink">
                  Locatie {idx + 1}{loc.type ? ` - ${LOCATION_TYPES.find(t => t.value === loc.type)?.label ?? loc.type}` : ''}
                </p>
                {data.locations.length > 1 && (
                  <button onClick={() => removeEntry(loc.id)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-3">

                {/* Type locatie */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink-2">Type locatie</label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {LOCATION_TYPES.map(t => (
                      <button key={t.value} type="button"
                        onClick={() => updEntry(loc.id, 'type', t.value)}
                        className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-left transition-all ${
                          loc.type === t.value ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
                        }`}>
                        <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                          loc.type === t.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                        }`} />
                        <span className="text-sm text-ink">{t.label}</span>
                      </button>
                    ))}
                  </div>
                  {loc.type === 'anders' && (
                    <Input label="Omschrijving" value={loc.typeOther}
                      onChange={e => updEntry(loc.id, 'typeOther', e.target.value)}
                      placeholder="Bijv: parkeerplaats, loods, atelier..." />
                  )}
                </div>

                {/* Eigendomsvorm */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink-2">Eigendomsvorm</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'koop', label: 'Koop / eigendom' },
                      { value: 'huur', label: 'Huur' },
                      { value: 'anders', label: 'Anders' },
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => updEntry(loc.id, 'ownership', opt.value)}
                        className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm flex-1 justify-center transition-all ${
                          loc.ownership === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800 font-medium' : 'border-sand-300 bg-white text-ink-3 hover:border-sage-300'
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Adres (V42: gestructureerd) */}
                {loc.address && !loc.street && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                    ⚠️ Dit adres is afkomstig uit een eerdere versie van WilsApp. Controleer en vul postcode en plaats aan.
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Input label="Straatnaam" value={loc.street}
                    onChange={e => updEntry(loc.id, 'street', e.target.value)}
                    placeholder="Buys Ballotstraat" />
                  <Input label="Huisnummer" value={loc.houseNumber}
                    onChange={e => updEntry(loc.id, 'houseNumber', e.target.value)}
                    placeholder="28" />
                </div>
                <Input label="Huisnummer toevoeging (optioneel)" value={loc.houseNumberAddition}
                  onChange={e => updEntry(loc.id, 'houseNumberAddition', e.target.value)}
                  placeholder="Bijv: A, bis" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Postcode" value={loc.postalCode}
                    onChange={e => updEntry(loc.id, 'postalCode', e.target.value)}
                    placeholder="1234 AB" />
                  <Input label="Plaats" value={loc.city}
                    onChange={e => updEntry(loc.id, 'city', e.target.value)}
                    placeholder="Heerhugowaard" />
                </div>

                {/* ── Koop-velden ──────────────────────────────────────── */}
                {loc.ownership === 'koop' && (
                  <div className="flex flex-col gap-3 animate-slide-down border-t border-sand-200 pt-3">
                    <p className="text-xs font-semibold text-ink-3 uppercase tracking-wide">Hypotheek</p>

                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-ink-2">Hypotheekverstrekker</label>
                      <select value={loc.mortgageProvider}
                        onChange={e => updEntry(loc.id, 'mortgageProvider', e.target.value)}
                        className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors">
                        <option value="">Geen / onbekend</option>
                        {MORTGAGE_PROVIDERS.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    {loc.mortgageProvider === 'Anders' && (
                      <Input label="Naam hypotheekverstrekker" value={loc.mortgageProviderOther}
                        onChange={e => updEntry(loc.id, 'mortgageProviderOther', e.target.value)}
                        placeholder="Naam van de verstrekker" />
                    )}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input label="Hypotheeknummer" value={loc.mortgageNumber}
                        onChange={e => updEntry(loc.id, 'mortgageNumber', e.target.value)}
                        placeholder="Bijv: HYP-123456" />
                      <Input label="Contactgegevens bank (optioneel)" value={loc.mortgageContact}
                        onChange={e => updEntry(loc.id, 'mortgageContact', e.target.value)}
                        placeholder="Telefoon / e-mail" />
                    </div>

                    <p className="text-xs font-semibold text-ink-3 uppercase tracking-wide">Overlijdensrisicoverzekering (ORV)</p>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes',     label: 'Ja' },
                        { value: 'no',      label: 'Nee' },
                        { value: 'unknown', label: 'Onbekend' },
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          onClick={() => updEntry(loc.id, 'hasOrv', opt.value)}
                          className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm flex-1 justify-center transition-all ${
                            loc.hasOrv === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800 font-medium' : 'border-sand-300 bg-white text-ink-3 hover:border-sage-300'
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {loc.hasOrv === 'yes' && (
                      <div className="flex flex-col gap-3 animate-slide-down">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <SelectWithOther label="ORV verzekeraar" options={ORV_INSURERS}
                            value={loc.orvInsurer}
                            onChange={v => updEntry(loc.id, 'orvInsurer', v)}
                            placeholder="Kies verzekeraar..."
                            otherOptionLabel="Anders"
                            otherFieldLabel="Naam verzekeraar"
                            otherPlaceholder="Naam verzekeraar" />
                          <Input label="ORV polisnummer" value={loc.orvPolicyNr}
                            onChange={e => updEntry(loc.id, 'orvPolicyNr', e.target.value)}
                            placeholder="Bijv: ORV-987654" />
                        </div>
                        <Input label="ORV contactgegevens (optioneel)" value={loc.orvContact}
                          onChange={e => updEntry(loc.id, 'orvContact', e.target.value)}
                          placeholder="Telefoon / e-mail verzekeraar" />
                      </div>
                    )}
                  </div>
                )}

                {/* ── Huur-velden ──────────────────────────────────────── */}
                {loc.ownership === 'huur' && (
                  <div className="flex flex-col gap-3 animate-slide-down border-t border-sand-200 pt-3">
                    <p className="text-xs font-semibold text-ink-3 uppercase tracking-wide">Huurgegevens</p>
                    <Input label="Verhuurder / beheerder" value={loc.landlord}
                      onChange={e => updEntry(loc.id, 'landlord', e.target.value)}
                      placeholder="Naam verhuurder of beheerder" />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input label="Contractnummer (optioneel)" value={loc.contractNr}
                        onChange={e => updEntry(loc.id, 'contractNr', e.target.value)}
                        placeholder="Bijv: HV-2024-0042" />
                      <Input label="Contactgegevens verhuurder" value={loc.landlordContact}
                        onChange={e => updEntry(loc.id, 'landlordContact', e.target.value)}
                        placeholder="Telefoon / e-mail" />
                    </div>
                  </div>
                )}

                {/* ── Altijd aanwezig ───────────────────────────────────── */}
                <div className="border-t border-sand-200 pt-3 flex flex-col gap-3">
                  <Input label="Sleutels aanwezig bij" value={loc.keys}
                    onChange={e => updEntry(loc.id, 'keys', e.target.value)}
                    placeholder="Bijv: thuis, bij buurvrouw, in kantoor..." />

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-ink-2">Documenten aanwezig bij</label>
                    <select value={loc.docsLocation}
                      onChange={e => updEntry(loc.id, 'docsLocation', e.target.value)}
                      className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors">
                      <option value="">Selecteer...</option>
                      {DOCS_LOCATIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  <Input label="Bijzonderheden (optioneel)" value={loc.notes}
                    onChange={e => updEntry(loc.id, 'notes', e.target.value)}
                    placeholder="Bijv: verhuurd aan derde, bevat waardevolle goederen..." />
                </div>

              </div>
            </Card>
          ))}

          <Button variant="outline" onClick={addEntry} icon={<Plus className="h-4 w-4" />}>
            Locatie toevoegen
          </Button>
        </div>
      )}
    </div>
  )
}
